import { useCallback, useState } from 'react';

import useCSRFToken from 'hooks/use-csrf-token';

import { customInstance } from 'state/custom-instance';

export interface LoginType {
  username: string;
  password: string;
}

export interface UseFreelancerLoginHandler {
  // Will return true in case the login process succeeded
  login: (values: LoginType) => Promise<boolean>;
  initialValues: LoginType;
  isLoading: boolean;
  error: Error | null;
}

const useFreelancerLogin = (): UseFreelancerLoginHandler => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const csrfToken = useCSRFToken();

  const login = useCallback(
    async (_values: LoginType): Promise<boolean> => {
      const loginPayloadFormData = new FormData();
      loginPayloadFormData.append('username', _values.username);
      loginPayloadFormData.append('password', _values.password);
      loginPayloadFormData.append('csrfmiddlewaretoken', csrfToken);
      setIsLoading(true);
      setError(null);
      try {
        const response = await customInstance({
          data: loginPayloadFormData,
          method: 'POST',
          url: '/login/',
          withCredentials: true,
        });

        // In case we successfully logged in, we will be redirected to another url different that /login/
        if (
          typeof response.request.responseURL === 'string' &&
          !(response.request.responseURL as string).endsWith('/login/')
        ) {
          return true;
        }

        setError(new Error('Something went wrong'));
        return false;
      } catch (_) {
        setError(new Error('Something went wrong'));

        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [csrfToken],
  );
  const initialValues: LoginType = { password: '', username: '' };
  return {
    error,
    initialValues,
    isLoading,
    login,
  };
};

export default useFreelancerLogin;
